import { useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import IconButtonMUI from "@mui/material/IconButton";
import { useMediaQuery } from "@mui/material";
import { X } from "lucide-react";
import ModalMedia from "../../../components/Modal/ModalMedia";
import AnnotationsChatInput from "./AnnotationsChatInput";
import AnnotationMessageList from "./AnnotationMessageList";
import { MIN_WIDTH_SCREEN_TO_SHOW_CHAT } from "../../../utils/constants";
import { INPUT_COLOR_BORDER } from "../../../utils/colors";
import { useRebrandBanner } from "../../../hooks/useRebrandBanner";
import { useTranslationApp } from "../../../lib/i18next";
import { useDispatchApp, useSelectorApp } from "../../../lib/redux";
import { useAnnotationsThread } from "../../../hooks/useAnnotationsThread";
import {
  ANNOTATIONS_MESSAGE_EDIT,
  ANNOTATIONS_MESSAGE_DELETE,
  ANNOTATIONS_SIDEBAR_ADVERTISER_HEADER,
  ANNOTATIONS_SIDEBAR_AGENCY_HEADER,
  ANNOTATIONS_SIDEBAR_CLOSE,
} from "../../../i18n/keysTranslations";
import { startGetAwsDownloadUrl } from "../../../actions/user";
import {
  FILE_FORMATS,
  IMAGE_FORMATS,
  VIDEO_FORMATS,
} from "../../../utils/constants";
import { getExtensionFile } from "../../../utils/string";
import { canManageAnnotationMessage } from "../utils/annotationMessageActions";

const AnnotationsSidebar = ({
  target = null,
  isVisible = true,
  onClose = () => {},
  ownersByID = {},
}) => {
  const { t } = useTranslationApp();
  const dispatch = useDispatchApp();
  const currentOwnerUid = useSelectorApp((state) => state.auth.user?.uid);
  const isLargeScreen = useMediaQuery(
    `(min-width: ${MIN_WIDTH_SCREEN_TO_SHOW_CHAT}px)`
  );
  const { totalTopOffsetPx } = useRebrandBanner();
  const [mediaPreview, setMediaPreview] = useState({ open: false, url: null });
  const [messageMenuAnchor, setMessageMenuAnchor] = useState(null);
  const [menuAnnotationID, setMenuAnnotationID] = useState(null);

  const {
    allAnnotations,
    isAnnotationsLoading,
    hasNoAnnotations,
    editingAnnotation,
    isSending,
    messagesScrollRef,
    onSendAnnotation,
    onStartEditAnnotation,
    onCancelEditAnnotation,
    onDeleteAnnotation,
  } = useAnnotationsThread({
    target,
    isVisible,
    currentOwnerUid,
  });

  const headerLabel = target?.isAgency
    ? t(ANNOTATIONS_SIDEBAR_AGENCY_HEADER, { name: target?.displayName || "" })
    : t(ANNOTATIONS_SIDEBAR_ADVERTISER_HEADER, {
        name: target?.displayName || "",
      });

  const onDownloadFile = async ({ url }) => {
    if (!url) return;
    await dispatch(startGetAwsDownloadUrl(url));
  };

  const onOpenFile = ({ file }) => {
    if (!file?.url) return;

    const extension = getExtensionFile(file?.url || file?.name || "");
    const isImage = IMAGE_FORMATS.includes(extension);
    const isVideo = VIDEO_FORMATS.includes(extension);
    const isPdf = FILE_FORMATS.includes(extension);

    if (isImage || isVideo) {
      setMediaPreview({ open: true, url: file.url });
      return;
    }

    if (isPdf) {
      window.open(file.url, "_blank", "noopener,noreferrer");
      return;
    }

    window.open(file.url, "_blank", "noopener,noreferrer");
  };

  const onCloseMediaPreview = () => {
    setMediaPreview((prev) => ({ ...prev, open: false }));
  };

  const onCloseMessageMenu = () => {
    setMessageMenuAnchor(null);
    setMenuAnnotationID(null);
  };

  const onOpenMessageMenu = ({ event, annotationID }) => {
    setMessageMenuAnchor(event.currentTarget);
    setMenuAnnotationID(annotationID);
  };

  if (!target) return null;

  return (
    <Box
      sx={{
        width: { xs: "100%", sm: 400 },
        minWidth: { xs: "100%", sm: 400 },
        maxWidth: { xs: "100%", sm: 400 },
        height: isLargeScreen ? `calc(100vh - ${totalTopOffsetPx}px)` : "100vh",
        borderLeft: isLargeScreen ? `1px solid ${INPUT_COLOR_BORDER}` : "none",
        display: isVisible ? "flex" : "none",
        flexDirection: "column",
        backgroundColor: "background.paper",
        position: isLargeScreen ? "sticky" : "fixed",
        top: isLargeScreen ? totalTopOffsetPx : 0,
        alignSelf: isLargeScreen ? "flex-start" : undefined,
        right: isLargeScreen ? "auto" : 0,
        bottom: isLargeScreen ? "auto" : 0,
        left: isLargeScreen ? "auto" : "auto",
        zIndex: isLargeScreen ? "auto" : 1299,
        boxShadow: isLargeScreen ? 0 : 8,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          pr: 1.5,
          pl: 2,
          pt: isLargeScreen ? 1.5 : 1,
          pb: 1,
          backgroundColor: "#FFF",
          borderBottom: `1px solid ${INPUT_COLOR_BORDER}`,
          minHeight: 52,
          flexShrink: 0,
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 600,
            lineHeight: 1.3,
            mt: isLargeScreen ? 1 : 0,
            flex: 1,
            minWidth: 0,
          }}
        >
          {headerLabel}
        </Typography>
        <IconButtonMUI
          onClick={onClose}
          aria-label={t(ANNOTATIONS_SIDEBAR_CLOSE)}
          size="small"
          sx={{
            flexShrink: 0,
            p: 0.5,
            color: "text.primary",
            boxShadow: "none",
            border: "none",
            backgroundColor: "transparent",
            "&:hover": {
              backgroundColor: "transparent",
              boxShadow: "none",
            },
          }}
        >
          <X size={17} />
        </IconButtonMUI>
      </Box>

      <AnnotationMessageList
        messagesScrollRef={messagesScrollRef}
        isAnnotationsLoading={isAnnotationsLoading}
        hasNoAnnotations={hasNoAnnotations}
        allAnnotations={allAnnotations}
        currentOwnerUid={currentOwnerUid}
        ownersByID={ownersByID}
        t={t}
        onDownloadFile={onDownloadFile}
        onOpenFile={onOpenFile}
        onOpenMessageMenu={onOpenMessageMenu}
      />

      <AnnotationsChatInput
        onSend={onSendAnnotation}
        onCancelEdit={onCancelEditAnnotation}
        brandName={target?.displayName}
        ownersByID={ownersByID}
        editingAnnotation={editingAnnotation}
        isProcessing={isSending}
      />

      <Menu
        anchorEl={messageMenuAnchor}
        open={Boolean(messageMenuAnchor)}
        onClose={onCloseMessageMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem
          onClick={() => {
            const annotation = allAnnotations.find(
              (item) => item.annotationID === menuAnnotationID
            );
            if (
              annotation &&
              canManageAnnotationMessage({
                annotation,
                currentOwnerUid,
              })
            ) {
              onStartEditAnnotation(annotation);
              onCloseMessageMenu();
            }
          }}
        >
          {t(ANNOTATIONS_MESSAGE_EDIT)}
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (!menuAnnotationID) return;

            const annotation = allAnnotations.find(
              (item) => item.annotationID === menuAnnotationID
            );

            if (
              !canManageAnnotationMessage({
                annotation,
                currentOwnerUid,
              })
            ) {
              return;
            }

            onDeleteAnnotation({ annotationID: menuAnnotationID });
            onCloseMessageMenu();
          }}
        >
          {t(ANNOTATIONS_MESSAGE_DELETE)}
        </MenuItem>
      </Menu>

      <ModalMedia
        modalOpen={mediaPreview.open}
        onCloseModal={onCloseMediaPreview}
        url={mediaPreview.url}
        showEditButton={false}
        showShareButton={false}
        showDeleteButton={false}
        showDownloadButton={true}
      />
    </Box>
  );
};

export default AnnotationsSidebar;
